// The "Claim receipts" folder: ATLAS claim receipts kept against the fee slip or
// disbursement they were claimed for (Emma, 26 Oct 2026).
//
// An ATLAS receipt is a one-page "RECIPIENT CREATED TAX INVOICE". Its text, as pdf.js reads
// it, carries the claim as label / value pairs:
//
//   Claim ID 6472687 · Your Claim Reference INV 7772 JONES · File/Ext 25A865271/0
//   Your File Reference 501179 · Client Name ISAIHA JOSEPH JONES · Practitioner PATRICK CROWLE
//   Submitted Date 23/10/2026 11:47:49 · DISB51 PSYCHOL REP+JAIL VISIT · 772.73 77.27 850.00
//
// The claim reference is whatever was typed in ATLAS - usually the invoice or fee slip
// number - so that is matched first, then the client's name and the amount. A match is only
// a suggestion: the person uploading confirms it, or leaves the receipt in the folder.
//
// One shared record per firm (vals_drafts, form type claim_receipts), used by both the Fee
// Slips & Invoices page and the Disbursements page, so a receipt uploaded on either can be
// kept against a fee slip or a disbursement.

export const CLAIM_RECEIPTS_FORM_TYPE = "claim_receipts";
const DRAFT_IDS = {
  vals: "00000000-0000-4000-8000-00000000c5a0",
  lacw: "00000000-0000-4000-8000-00000000c5a1",
  rga: "00000000-0000-4000-8000-00000000c5a2",
  eas: "00000000-0000-4000-8000-00000000c5a3",
  admin: "00000000-0000-4000-8000-00000000c5a4",
};
export const claimReceiptsDraftId = (brand) => DRAFT_IDS[brand] || DRAFT_IDS.lacw;

export const LINK_KINDS = ["fee_slip", "disbursement"];

const text = (value, max = 300) => String(value ?? "").replace(/\s+/g, " ").trim().slice(0, max);

// ---- Reading a receipt --------------------------------------------------------------------

const LABELS = [
  "Service Provider", "Business Address", "Claim ID", "Your Claim Reference", "Proforma Invoice ID", "File/Ext", "Your File Reference",
  "Client Name", "Practitioner", "Work Performed", "Submitted Date", "Date of Issue", "Payment Method",
];

// The value printed after a label: the next line, unless that line is itself a label.
function valueAfter(lines, label) {
  const index = lines.findIndex((line) => line.toLowerCase() === label.toLowerCase());
  if (index < 0) return "";
  const next = lines[index + 1] || "";
  return LABELS.some((other) => other.toLowerCase() === next.toLowerCase()) ? "" : next;
}

const toIsoDay = (value) => {
  const match = String(value || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  return match ? `${match[3]}-${match[2].padStart(2, "0")}-${match[1].padStart(2, "0")}` : "";
};

export function isClaimReceipt(rawText = "") {
  return /recipient created tax invoice/i.test(rawText) && /claim id/i.test(rawText);
}

export function parseClaimReceipt(rawText = "") {
  const lines = String(rawText || "").split(/\r?\n/).map((line) => text(line)).filter(Boolean);
  // The totals print first: GST exclusive, GST, GST inclusive, then the same again.
  const figures = lines.filter((line) => /^\d{1,3}(?:,\d{3})*\.\d{2}$/.test(line)).map((line) => Number(line.replace(/,/g, "")));
  let amount = "";
  for (let i = 0; i + 2 < figures.length; i += 1) {
    const [ex, gst, inc] = figures.slice(i, i + 3);
    if (Math.abs(ex + gst - inc) <= 0.02) { amount = inc.toFixed(2); break; }
  }
  if (!amount && figures.length) amount = Math.max(...figures).toFixed(2);
  const work = (() => {
    const index = lines.findIndex((line) => /^(?:DISB|[A-Z]{2,6})\d{1,3}$/.test(line));
    return index >= 0 ? text(`${lines[index]} ${lines[index + 1] || ""}`, 120) : "";
  })();
  return {
    serviceProvider: text(valueAfter(lines, "Service Provider"), 120),
    claimId: text(valueAfter(lines, "Claim ID"), 40),
    claimRef: text(valueAfter(lines, "Your Claim Reference"), 120),
    fileExt: text(valueAfter(lines, "File/Ext"), 40),
    fileRef: text(valueAfter(lines, "Your File Reference"), 60),
    client: text(valueAfter(lines, "Client Name"), 120),
    practitioner: text(valueAfter(lines, "Practitioner"), 120),
    submitted: toIsoDay(valueAfter(lines, "Submitted Date") || valueAfter(lines, "Date of Issue")),
    work,
    amount,
    draft: /tax invoice\s*-\s*draft/i.test(rawText),
  };
}

// ---- Suggesting what a receipt is for ------------------------------------------------------

const nameWords = (value) => new Set(String(value || "").toLowerCase().replace(/[^a-z\s'-]/g, " ").split(/\s+/)
  .map((word) => word.replace(/['-]/g, "")).filter((word) => word.length >= 2));
const digitRuns = (value) => (String(value || "").match(/\d{3,}/g) || []).map((run) => run.replace(/^0+/, "")).filter((run) => run.length >= 3);

// candidates: [{ kind, id, label, clientName, invoiceRef, amount }]. Returns the plausible
// ones, best first, each with its score and why.
export function suggestLinks(receipt = {}, candidates = []) {
  const refRuns = new Set(digitRuns(receipt.claimRef));
  const client = nameWords(receipt.client);
  const amount = Number(receipt.amount) || 0;
  return candidates
    .map((candidate) => {
      const reasons = [];
      let score = 0;
      const invoice = digitRuns(candidate.invoiceRef).find((run) => refRuns.has(run));
      if (invoice) { score += 6; reasons.push(`invoice ${invoice}`); }
      const shared = [...nameWords(candidate.clientName)].filter((word) => client.has(word));
      if (shared.length >= 2) { score += 4; reasons.push("client name"); }
      else if (shared.length === 1) { score += 2; reasons.push("client surname"); }
      const theirs = Number(candidate.amount) || 0;
      if (amount && theirs && Math.abs(theirs - amount) <= 0.01) { score += 3; reasons.push("amount"); }
      return { ...candidate, score, reasons };
    })
    // An amount alone is not a match; a name or the invoice number has to agree too.
    .filter((candidate) => candidate.reasons.some((reason) => reason !== "amount"))
    .sort((a, b) => b.score - a.score);
}

// ---- The folder record ---------------------------------------------------------------------

export const emptyClaimReceipts = () => ({ receipts: [] });

export function normaliseClaimReceipts(formData) {
  const receipts = (Array.isArray(formData?.receipts) ? formData.receipts : [])
    .filter((receipt) => receipt && receipt.id && receipt.fileUrl)
    .map((receipt) => ({
      ...receipt,
      links: (Array.isArray(receipt.links) ? receipt.links : []).filter((link) => LINK_KINDS.includes(link?.kind) && link.id),
    }));
  return { receipts };
}

export function buildReceipt(fields = {}, file = {}, meta = {}) {
  return {
    id: meta.id || `receipt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    claimId: text(fields.claimId, 40),
    claimRef: text(fields.claimRef, 120),
    fileExt: text(fields.fileExt, 40),
    fileRef: text(fields.fileRef, 60),
    client: text(fields.client, 120),
    practitioner: text(fields.practitioner, 120),
    submitted: text(fields.submitted, 10),
    work: text(fields.work, 120),
    amount: fields.amount ? Number(fields.amount) || 0 : 0,
    fileName: text(file.fileName, 200),
    fileUrl: text(file.fileUrl, 500),
    filePath: text(file.filePath, 500),
    uploadedAt: meta.at || new Date().toISOString(),
    uploadedBy: text(meta.by, 120),
    links: [],
  };
}

const linkOf = (candidate) => ({ kind: candidate.kind, id: candidate.id, label: text(candidate.label, 200) });

// Every change returns a new record and is a single delta, so a colleague's save survives.
export function addReceipt(data, receipt, candidate = null) {
  if (data.receipts.some((item) => item.id === receipt.id)) return data;
  // The same ATLAS claim uploaded twice is kept once.
  if (receipt.claimId && data.receipts.some((item) => item.claimId === receipt.claimId)) return data;
  return { ...data, receipts: [{ ...receipt, links: candidate ? [linkOf(candidate)] : [] }, ...data.receipts] };
}

export function linkReceipt(data, receiptId, candidate) {
  return {
    ...data,
    receipts: data.receipts.map((receipt) => (receipt.id !== receiptId || receipt.links.some((link) => link.kind === candidate.kind && link.id === candidate.id)
      ? receipt
      : { ...receipt, links: [...receipt.links, linkOf(candidate)] })),
  };
}

export function unlinkReceipt(data, receiptId, kind, id) {
  return {
    ...data,
    receipts: data.receipts.map((receipt) => (receipt.id !== receiptId
      ? receipt
      : { ...receipt, links: receipt.links.filter((link) => !(link.kind === kind && link.id === id)) })),
  };
}

export function removeReceipt(data, receiptId) {
  return { ...data, receipts: data.receipts.filter((receipt) => receipt.id !== receiptId) };
}

// The receipts kept against one fee slip or disbursement.
export function receiptsFor(data, kind, id) {
  return (data?.receipts || []).filter((receipt) => receipt.links.some((link) => link.kind === kind && link.id === id));
}

export function isDuplicateClaim(data, claimId) {
  return Boolean(claimId) && (data?.receipts || []).some((receipt) => receipt.claimId === claimId);
}
